import React from 'react';
import { ScrollView } from 'react-native';
import { renderRules, RenderRules } from 'react-native-markdown-display';

/**
 * Render rules shared by every markdown surface in chat.
 *
 * A table row is `flexDirection: 'row'` of `flex: 1` cells. `flex: 1` lets a
 * cell shrink, but Yoga's default `minWidth: 'auto'` stops it shrinking below
 * its content, so a row of wide cells has a minimum width larger than the
 * bubble and paints past the right edge of a portrait screen. Scrolling the
 * table inside the bubble keeps every column reachable without rotating the
 * device.
 *
 * `flexGrow: 1` on the content container is what stops this regressing narrow
 * tables: a table that fits still stretches to the bubble width instead of
 * collapsing to its minimum, and only a genuinely wide one overflows and
 * becomes scrollable.
 *
 * `fence` and `code_block` are deliberately absent. They render as a single
 * <Text>, which wraps rather than overflowing, so giving them a horizontal
 * ScrollView would trade readable wrapped code for a swipe on every code block.
 */
function scrollWide(node: { key: string }, rendered: React.ReactNode): React.ReactNode {
  return (
    <ScrollView
      key={node.key}
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={{ flexGrow: 1 }}
      style={{ flexShrink: 1, minWidth: 0 }}
    >
      {rendered}
    </ScrollView>
  );
}

const defaultTable = renderRules.table!;

/**
 * Module-level constant, not a factory: `Markdown` lists `rules` in the
 * dependency array of the `useMemo` that builds its renderer, so a fresh object
 * per render would rebuild the renderer on every keystroke of a streamed reply.
 */
export const chatMarkdownRules: RenderRules = {
  table: (node, children, parent, styles, inheritedStyles) =>
    scrollWide(node, defaultTable(node, children, parent, styles, inheritedStyles)),
};
